import React, { useEffect, useState } from 'react';
import './AdminPanel.scss';
import {
  fetchConfig, saveConfig, previewConversation, fetchVoices, previewVoice,
} from '../../services/api';

const AdminPanel = () => {
  const [config, setConfig] = useState(null);
  const [status, setStatus] = useState('loading'); // loading | ready | saving | error
  const [message, setMessage] = useState('');

  // 声の一覧・エンジン
  const [engine, setEngine] = useState('gemini');
  const [voiceList, setVoiceList] = useState([]);
  const [previewingVoice, setPreviewingVoice] = useState(null); // 試聴中のキャラid

  // プレビュー用
  const [previewKeyword, setPreviewKeyword] = useState('');
  const [previewLines, setPreviewLines] = useState(null);
  const [previewStatus, setPreviewStatus] = useState('idle'); // idle | loading | error
  const [previewError, setPreviewError] = useState('');

  useEffect(() => {
    fetchConfig()
      .then((cfg) => {
        setConfig(cfg);
        setStatus('ready');
      })
      .catch((e) => {
        setStatus('error');
        setMessage(e.message);
      });
    fetchVoices()
      .then((v) => {
        setEngine(v.engine);
        setVoiceList(v.voices || []);
      })
      .catch(() => { /* 声一覧は取れなくても編集は可能 */ });
  }, []);

  // ── 更新ヘルパー ──────────────────────────────
  const updateField = (key, value) => {
    setConfig((prev) => ({ ...prev, [key]: value }));
  };

  const updateChar = (idx, key, value) => {
    setConfig((prev) => {
      const characters = prev.characters.map((c, i) =>
        i === idx ? { ...c, [key]: value } : c
      );
      return { ...prev, characters };
    });
  };

  // 声設定（character.voice[engine].{name,rate,tone}）を更新
  const updateVoice = (charIdx, key, value) => {
    setConfig((prev) => {
      const characters = prev.characters.map((c, i) => {
        if (i !== charIdx) return c;
        const voice = { ...(c.voice || {}) };
        voice[engine] = { ...(voice[engine] || {}), [key]: value };
        return { ...c, voice };
      });
      return { ...prev, characters };
    });
  };

  const handleVoicePreview = async (charIdx) => {
    const c = config.characters[charIdx];
    const v = (c.voice || {})[engine] || {};
    setPreviewingVoice(c.id);
    try {
      const url = await previewVoice(c.id, {
        name: v.name,
        rate: v.rate,
        tone: v.tone,
      });
      const audio = new Audio(url);
      audio.play().catch(() => {});
    } catch (e) {
      setMessage(e.message);
      setStatus('error');
    } finally {
      setPreviewingVoice(null);
    }
  };

  const updateCatchphrase = (charIdx, cpIdx, value) => {
    setConfig((prev) => {
      const characters = prev.characters.map((c, i) => {
        if (i !== charIdx) return c;
        const catchphrases = c.catchphrases.map((cp, j) => (j === cpIdx ? value : cp));
        return { ...c, catchphrases };
      });
      return { ...prev, characters };
    });
  };

  const addCatchphrase = (charIdx) => {
    setConfig((prev) => {
      const characters = prev.characters.map((c, i) =>
        i === charIdx ? { ...c, catchphrases: [...c.catchphrases, ''] } : c
      );
      return { ...prev, characters };
    });
  };

  const removeCatchphrase = (charIdx, cpIdx) => {
    setConfig((prev) => {
      const characters = prev.characters.map((c, i) => {
        if (i !== charIdx) return c;
        return { ...c, catchphrases: c.catchphrases.filter((_, j) => j !== cpIdx) };
      });
      return { ...prev, characters };
    });
  };

  const updateRule = (idx, value) => {
    setConfig((prev) => ({
      ...prev,
      rules: prev.rules.map((r, i) => (i === idx ? value : r)),
    }));
  };

  const addRule = () => {
    setConfig((prev) => ({ ...prev, rules: [...prev.rules, ''] }));
  };

  const removeRule = (idx) => {
    setConfig((prev) => ({ ...prev, rules: prev.rules.filter((_, i) => i !== idx) }));
  };

  const updateFlow = (key, value) => {
    setConfig((prev) => ({ ...prev, flow: { ...prev.flow, [key]: value } }));
  };

  // ── 保存 ──────────────────────────────────────
  const handleSave = async () => {
    setStatus('saving');
    setMessage('');
    try {
      // 空文字の口癖・ルールは除去してから保存
      const cleaned = {
        ...config,
        rules: config.rules.filter((r) => r.trim()),
        characters: config.characters.map((c) => ({
          ...c,
          catchphrases: c.catchphrases.filter((cp) => cp.trim()),
        })),
      };
      await saveConfig(cleaned);
      setConfig(cleaned);
      setStatus('ready');
      setMessage('保存しました');
      setTimeout(() => setMessage(''), 2500);
    } catch (e) {
      setStatus('error');
      setMessage(e.message);
    }
  };

  // ── プレビュー生成（編集中の設定をそのまま使う。未保存でもOK）──
  const handlePreview = async () => {
    if (!previewKeyword.trim()) {
      setPreviewError('お題を入力してください');
      return;
    }
    setPreviewStatus('loading');
    setPreviewError('');
    try {
      const cleaned = {
        ...config,
        rules: config.rules.filter((r) => r.trim()),
        characters: config.characters.map((c) => ({
          ...c,
          catchphrases: c.catchphrases.filter((cp) => cp.trim()),
        })),
      };
      const result = await previewConversation(previewKeyword, cleaned);
      setPreviewLines(result.lines);
      setPreviewStatus('idle');
    } catch (e) {
      setPreviewStatus('error');
      setPreviewError(e.message);
    }
  };

  const speakerName = (id) => {
    const c = (config?.characters || []).find((ch) => ch.id === id);
    return c ? c.name || c.id : id;
  };

  if (status === 'loading') {
    return <div className="admin-panel"><p className="admin-loading">読み込み中...</p></div>;
  }

  if (!config) {
    return (
      <div className="admin-panel">
        <p className="admin-error">設定を読み込めませんでした：{message}</p>
      </div>
    );
  }

  return (
    <div className="admin-panel">
      <header className="admin-header">
        <h1>プロンプト管理</h1>
        <div className="admin-actions">
          {message && (
            <span className={`admin-message ${status === 'error' ? 'is-error' : 'is-ok'}`}>
              {message}
            </span>
          )}
          <a className="admin-link" href="/">アプリを開く ↗</a>
          <button
            type="button"
            className="admin-save"
            onClick={handleSave}
            disabled={status === 'saving'}
          >
            {status === 'saving' ? '保存中...' : '保存'}
          </button>
        </div>
      </header>

      {/* プレビュー */}
      <section className="admin-section admin-preview">
        <h2>プレビュー（この設定で会話を生成）</h2>
        <p className="admin-hint">
          編集中の内容（未保存でもOK）でその場で会話を生成します。お題を入れて試してください。
        </p>
        <div className="admin-preview-bar">
          <input
            className="admin-input"
            value={previewKeyword}
            placeholder="お題：例）夏休み、ゲーム、恋バナ..."
            onChange={(e) => setPreviewKeyword(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handlePreview(); }}
          />
          <button
            type="button"
            className="admin-save"
            onClick={handlePreview}
            disabled={previewStatus === 'loading'}
          >
            {previewStatus === 'loading' ? '生成中...' : 'プレビュー生成'}
          </button>
        </div>
        {previewError && <p className="admin-preview-error">{previewError}</p>}
        {previewLines && (
          <div className="admin-preview-result">
            {previewLines.map((line, i) => (
              <div className="admin-preview-line" key={i}>
                <span className="admin-preview-speaker">{speakerName(line.speaker)}</span>
                <span className="admin-preview-text">{line.text}</span>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* 世界観 */}
      <section className="admin-section">
        <h2>世界観・番組設定</h2>
        <textarea
          className="admin-textarea"
          rows={3}
          value={config.world || ''}
          onChange={(e) => updateField('world', e.target.value)}
        />
      </section>

      {/* キャラクター */}
      <section className="admin-section">
        <h2>キャラクター</h2>
        <div className="admin-chars">
          {config.characters.map((c, idx) => (
            <div className="admin-char-card" key={c.id || idx}>
              <div className="admin-char-head">
                <span className="admin-char-id">{c.id}</span>
                <input
                  className="admin-input admin-char-name"
                  value={c.name || ''}
                  placeholder="名前"
                  onChange={(e) => updateChar(idx, 'name', e.target.value)}
                />
              </div>

              <label className="admin-label">役割</label>
              <input
                className="admin-input"
                value={c.role || ''}
                onChange={(e) => updateChar(idx, 'role', e.target.value)}
              />

              <label className="admin-label">性格</label>
              <textarea
                className="admin-textarea"
                rows={2}
                value={c.personality || ''}
                onChange={(e) => updateChar(idx, 'personality', e.target.value)}
              />

              <label className="admin-label">喋り方・トーン</label>
              <input
                className="admin-input"
                value={c.speaking_style || ''}
                placeholder="（任意）例：早口・丁寧語 など"
                onChange={(e) => updateChar(idx, 'speaking_style', e.target.value)}
              />

              <label className="admin-label">口癖</label>
              <div className="admin-catchphrases">
                {c.catchphrases.map((cp, cpIdx) => (
                  <div className="admin-cp-row" key={cpIdx}>
                    <input
                      className="admin-input"
                      value={cp}
                      onChange={(e) => updateCatchphrase(idx, cpIdx, e.target.value)}
                    />
                    <button
                      type="button"
                      className="admin-remove"
                      onClick={() => removeCatchphrase(idx, cpIdx)}
                      aria-label="削除"
                    >
                      ×
                    </button>
                  </div>
                ))}
                <button type="button" className="admin-add" onClick={() => addCatchphrase(idx)}>
                  ＋ 口癖を追加
                </button>
              </div>

              {/* 声（ナレーター）設定 */}
              <div className="admin-voice">
                <div className="admin-voice-head">
                  <span className="admin-voice-title">声（{engine}）</span>
                  <button
                    type="button"
                    className="admin-voice-play"
                    onClick={() => handleVoicePreview(idx)}
                    disabled={previewingVoice === c.id}
                  >
                    {previewingVoice === c.id ? '生成中...' : '▶ 試聴'}
                  </button>
                </div>

                <div className="admin-voice-row">
                  <div className="admin-voice-field">
                    <label className="admin-label">声の種類</label>
                    <select
                      className="admin-input"
                      value={(c.voice?.[engine]?.name) || ''}
                      onChange={(e) => updateVoice(idx, 'name', e.target.value)}
                    >
                      <option value="">（選択）</option>
                      {voiceList.map((vn) => (
                        <option key={vn} value={vn}>{vn}</option>
                      ))}
                    </select>
                  </div>
                  <div className="admin-voice-field admin-voice-rate">
                    <label className="admin-label">話す速さ（1.0=標準）</label>
                    <input
                      className="admin-input"
                      type="number"
                      step="0.05"
                      min="0.5"
                      max="2"
                      value={(c.voice?.[engine]?.rate) ?? 1.0}
                      onChange={(e) => updateVoice(idx, 'rate', parseFloat(e.target.value))}
                    />
                  </div>
                </div>

                <label className="admin-label">トーン・話し方の指示</label>
                <textarea
                  className="admin-textarea"
                  rows={2}
                  value={(c.voice?.[engine]?.tone) || ''}
                  placeholder="例：落ち着いた低い声で、優しく話す"
                  onChange={(e) => updateVoice(idx, 'tone', e.target.value)}
                />
              </div>

              <label className="admin-label">してほしい / NG（任意）</label>
              <textarea
                className="admin-textarea"
                rows={2}
                value={c.notes || ''}
                onChange={(e) => updateChar(idx, 'notes', e.target.value)}
              />
            </div>
          ))}
        </div>
      </section>

      {/* ルール */}
      <section className="admin-section">
        <h2>会話ルール</h2>
        <div className="admin-rules">
          {config.rules.map((r, idx) => (
            <div className="admin-cp-row" key={idx}>
              <input
                className="admin-input"
                value={r}
                onChange={(e) => updateRule(idx, e.target.value)}
              />
              <button
                type="button"
                className="admin-remove"
                onClick={() => removeRule(idx)}
                aria-label="削除"
              >
                ×
              </button>
            </div>
          ))}
          <button type="button" className="admin-add" onClick={addRule}>
            ＋ ルールを追加
          </button>
        </div>
      </section>

      {/* 話の流れ */}
      <section className="admin-section">
        <h2>話の流れ</h2>
        <label className="admin-label">最初の会話の指示（{'{keyword}'} が入力キーワードに置換されます）</label>
        <textarea
          className="admin-textarea"
          rows={2}
          value={config.flow?.start_instruction || ''}
          onChange={(e) => updateFlow('start_instruction', e.target.value)}
        />
        <label className="admin-label">「続きを聞く」の続き指示</label>
        <textarea
          className="admin-textarea"
          rows={3}
          value={config.flow?.continue_instruction || ''}
          onChange={(e) => updateFlow('continue_instruction', e.target.value)}
        />
      </section>

      <footer className="admin-footer">
        <button
          type="button"
          className="admin-save"
          onClick={handleSave}
          disabled={status === 'saving'}
        >
          {status === 'saving' ? '保存中...' : '保存'}
        </button>
      </footer>
    </div>
  );
};

export default AdminPanel;
